import { DocumentReference, DocumentData } from "@angular/fire/firestore";
import { ProductType } from "../product/enums/product-type.enum";
import * as moment from "moment";
import { CitySummary } from "./models/city-summary.model";
import { EventSummary } from "./models/event-summary.model";
import { UserSummary } from "./models/user-summary.model";
import { ProductSummary } from "./models/product-summary.model";
import { OrderSummary } from "./models/order-summary.model";
import { VenueSummary } from "./models/venue-summary.model";
import { MenuSummary } from "./models/menu-summary.model";
export class Order {
  public readonly reference: DocumentReference;
  public readonly city: CitySummary;
  public readonly event: EventSummary;
  public readonly user: UserSummary;
  public readonly product: ProductSummary;
  public readonly summary: OrderSummary;
  public readonly venue: VenueSummary;
  public readonly menu: MenuSummary;
  public readonly vipPass: boolean = false;

  constructor(data: DocumentData, reference: DocumentReference) {
    this.reference = reference;
    //Step 1: Build User Data
    this.user = new UserSummary(data.user);

    //Step 2: Build Venue Data
    this.venue = new VenueSummary(data.venue);

    //Step 3: Build Order Summary
    this.summary = new OrderSummary(data.summary);

    //Step 4: Build City Data
    this.city = new CitySummary(data.city);

    this.event = new EventSummary(data.event);

    this.product = new ProductSummary(data.product);

    this.menu = data.menu ? new MenuSummary(data.menu) : null;
    if (this.event.timezone == null) this.updateTimezone();

    // console.log(data.vipPass);

    this.vipPass = data.vipPass;
  }

  toCSV() {
    var isTicket = this.product.type == ProductType.Ticket.valueOf();
    var csv = {
      "Purchase Date": moment(this.summary.purchasedAt).format("LL"),
      "Purchase Time": moment(this.summary.purchasedAt).format("LT"),
      City: this.city.name,
      Venue: this.venue.name,
      "Product Type": ProductType[this.product.type],
      "Product Name": this.product.name,
      "Event Date": moment(this.event.date).format("LL"),
      "Customer Name": this.user.name,
      "Customer Email": this.user.email,
      Units: this.summary.units,
      Price: this.summary.price,
      Subtotal: this.summary.subtotal,
      "Has Service Fee": this.summary.serviceFee != null,
      "Service Fee":
        this.summary.serviceFee == null ? 0 : this.summary.serviceFee,
      "Has Tip": this.summary.tip != null,
      Tip: this.summary.tip == null ? 0 : this.summary.tip,
      "Tip Recipient":
        this.menu != null && this.menu.tipRecipient != null
          ? this.menu.tipRecipient
          : "N/A",
      Total: this.summary.total,
      "Redeem Date":
        this.summary.redeemedAt != null
          ? moment(this.summary.redeemedAt).format("LL")
          : "N/A",
      "Has Been Redeemed": this.summary.redeemedAt != null,
      "Redeem Time":
        this.summary.redeemedAt != null
          ? moment(this.summary.redeemedAt).format("LT")
          : "N/A",
      "Ticket Name": this.event.name ?? "",
      orderId: this.reference.id
    };
    if (this.user.responses == null || this.user.responses.length == 0)
      return csv;
    let questions = this.user.responses.length;
    for (let i = 0; i < questions; i++) {
      csv["Question " + (i + 1)] = this.user.responses[i].question;
      csv["Response " + (i + 1)] = this.user.responses[i].response;
    }

    return csv;
  }

  productName(): string {
    return this.product.name;
  }

  async updateTimezone() {
    await this.reference.set(
      { event: { timezone: "America/Detroit" } },
      { merge: true }
    );
    return;
  }

  public mapOrderToMenu(): MenuOrderData[] {
    let x: MenuOrderData[] = [];
    if (this.menu == null) return x;
    for (let i = 0; i < this.menu.items.length; i++) {
      x.push(new MenuOrderData(this, i));
    }
    return x;
  }

  private get transactionFee(): number {
    return this.summary.total < 0.3
      ? this.summary.total
      : 0.3 + this.summary.total * 0.029;
  }

  public get fee(): number {
    return this.transactionFee + this.summary.serviceFee;
  }
}
export class MenuOrderData {
  private _purchasedAt: Date;
  private _orderId: String;
  private _username: String;
  private _userEmail: String;
  private _drinkName: String;
  private _drinkCategories: String;
  private _price: number;
  private _units: number;
  private _fee: number;
  private _tip: number;
  private _total: number;
  private _redeemedAt: Date;

  constructor(private order: Order, private index: number) {
    this._purchasedAt = order.summary.purchasedAt;
    this._orderId = order.reference.id;
    this._username = order.user.name;
    this._userEmail = order.user.email;
    this._drinkName = order.menu.items[index].itemName;
    this._drinkCategories = order.menu.items[index].categories.toString();
    this._price = order.menu.items[index].itemPrice;
    this._units = order.menu.items[index].units;
    this._fee = order.summary.serviceFee ?? 0;
    this._tip = order.summary.tip / order.menu.items.length ?? 0;

    this._total =
      order.menu.items[index].itemPrice * order.menu.items[index].units +
      this._fee +
      this._tip;
    this._redeemedAt = order.summary.redeemedAt;
  }

  public get purchasedAt(): Date {
    return this._purchasedAt;
  }
  public get orderId(): String {
    return this._orderId;
  }
  public get username(): String {
    return this._username;
  }
  public get userEmail(): String {
    return this._userEmail;
  }
  public get drinkName(): String {
    return this._drinkName;
  }
  public get drinkCategories(): String {
    return this._drinkCategories;
  }
  public get price(): number {
    return this._price;
  }
  public get units(): number {
    return this._units;
  }

  public get fee(): number {
    return this._fee;
  }
  public get tip(): number {
    return this._tip;
  }
  public get total(): number {
    return this._total;
  }
  public get redeemedAt(): Date {
    return this._redeemedAt;
  }
  toCSV(isAdmin: boolean) {
    var csv = {
      "Order ID": this.orderId,
      "Purchase Date": moment(this.purchasedAt).format("LL"),
      "Purchase Time": moment(this.purchasedAt).format("LT"),
      "Drink Name": this.drinkName,
      "Drink Categories": this.drinkCategories,
      "Customer Name": this.username,
      Fee: this.fee,
      Tip: this.tip,
      Price: this.price,
      Units: this.units,
      Total: this.total,
      "Tip Recipient": this.order.menu.tipRecipient ?? "N/A",
      "Has Been Redeemed": this.redeemedAt != null,
      "Redeem Date":
        this.redeemedAt != null ? moment(this.redeemedAt).format("LL") : "N/A",
      "Redeem Time":
        this.redeemedAt != null ? moment(this.redeemedAt).format("LT") : "N/A"
    };
    if (isAdmin) {
      csv["Customer Email"] = this.userEmail;
    }
    return csv;
  }
}
